// Status and priority glyphs are drawn here, not taken from the icon set (DESIGN-RULES 7 and 8). Shape first,
// colour second, so meaning never rests on colour alone (WCAG 1.4.1).

export const STATUS_GROUPS = ["backlog", "todo", "progress", "review", "done", "cancelled"] as const;
export type StatusGroup = (typeof STATUS_GROUPS)[number];
export const PRIORITIES = ["urgent", "high", "medium", "low", "none"] as const;
export type Priority = (typeof PRIORITIES)[number];

/** The colour token of a status group. */
export const statusVar = (g: StatusGroup) => `var(--status-${g})`;

type GlyphProps = { size?: number; className?: string; label?: string };
const a11y = (label?: string) => (label ? { role: "img" as const, "aria-label": label } : { "aria-hidden": true as const });

export function StatusGlyph({ group, size = 15, className, label }: GlyphProps & { group: StatusGroup }) {
  const common = { width: size, height: size, viewBox: "0 0 16 16", className, style: { color: statusVar(group) }, ...a11y(label) };
  switch (group) {
    case "backlog":
      return <svg {...common}><circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2.2 2.2" /></svg>;
    case "todo":
      return <svg {...common}><circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>;
    case "progress":
      return <svg {...common}><circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M8 4a4 4 0 0 1 0 8z" fill="currentColor" /></svg>;
    case "review":
      return <svg {...common}><circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M8 4a4 4 0 1 1-4 4h4z" fill="currentColor" /></svg>;
    case "done":
      return <svg {...common}><circle cx="8" cy="8" r="7" fill="currentColor" /><path d="m5.2 8.2 1.9 1.9 3.8-4" fill="none" stroke="var(--panel-solid)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    case "cancelled":
      return <svg {...common}><circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="m5.6 5.6 4.8 4.8M10.4 5.6l-4.8 4.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>;
  }
}

// A list id: the five default ids draw their bars; any other id (an agency's own priority, X11-F1-01) draws the none glyph.
export function PriorityGlyph({ priority, size = 16, className, label }: GlyphProps & { priority: string }) {
  const common = { width: size, height: size, viewBox: "0 0 16 16", className, ...a11y(label) };
  if (priority === "urgent") {
    return (
      <svg {...common}>
        <rect x="1.5" y="1.5" width="13" height="13" rx="3.5" fill="var(--prio-urgent)" />
        <path d="M8 4.6v4.3M8 11.3v.1" stroke="var(--panel-solid)" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  const n = priority === "high" ? 3 : priority === "medium" ? 2 : priority === "low" ? 1 : 0;
  const on = priority === "high" ? "var(--prio-high)" : priority === "medium" ? "var(--prio-medium)" : "var(--prio-low)";
  return (
    <svg {...common}>
      {[0, 1, 2].map((i) => (
        <rect key={i} x={2.5 + i * 4.2} y={10 - i * 3} width="2.6" height={4 + i * 3} rx="1" fill={i < n ? on : "var(--prio-off)"} />
      ))}
    </svg>
  );
}

/** The hourglass of a waiting or blocked flag. */
export function FlagGlyph({ size = 12, className, label }: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} {...a11y(label)}>
      <path d="M4.5 2h7M4.5 14h7M5.2 2c0 3 5.6 3.4 5.6 6s-5.6 3-5.6 6M10.8 2c0 3-5.6 3.4-5.6 6s5.6 3 5.6 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
